import React, { memo, useEffect, useState } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Form, FormGroup, FormText, FormFeedback, Row, Col, Label, Input } from 'reactstrap';
import { isEmpty, has } from 'lodash';
import ReactSelect from 'react-select';
import SweetAlert from 'react-bootstrap-sweetalert';
import FileUpload from '../../../components/forms/FileUpload';
import * as API from '../../../services/api';
import { isFile, Notification } from '../../../utils';

const schemaInvestorSelect = yup.object().shape({
  name: yup.string(),
  attribute: yup.string(),
  is_public: yup.boolean(),
  is_active: yup.boolean()
});

const FormAppSettings = (props) => {
  const { id } = useParams();

  const {
    watch,
    formState: { errors },
    handleSubmit,
    control
  } = useForm({
    defaultValues: { entity: { value: 'select', label: 'Select Entity' } },
    resolver: yupResolver(schemaInvestorSelect)
  });

  const [optionCountryCodes, setOptionCountryCodes] = useState([]);
  useEffect(() => {
    API.dataSets(['country_codes']).then((res) => {
      setOptionCountryCodes(res.data.country_codes);
    });
  }, []);

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const onSubmitAccountSettings = async (data) => {
    return null;
  };

  const [editable, setEditable] = useState(false);

  const enableEdit = () => {
    setEditable(!editable);
  };

  const onSubmitAppSettings = async (data) => {
    const fileCondition = (file) => {
      return file && isFile(file[0]) && !has(file[0], 'id');
    };

    const params = new URLSearchParams();

    if (fileCondition(data.company_logo)) {
      let res = await uploadFiles([data.company_logo[0]]);
      params.append('company_logo', res[0].id);
    } else {
      params.append('company_logo', !isEmpty(data.company_logo) ? data.company_logo[0].id : '');
    }

    if (fileCondition(data.company_banner)) {
      let res = await uploadFiles([data.company_banner[0]]);
      params.append('company_banner', res[0].id);
    } else {
      params.append(
        'company_banner',
        !isEmpty(data.company_banner) ? data.company_banner[0].id : ''
      );
    }

    params.append('company_background', data.company_background);
    API.updateIssuerAppSettings(id, params);
  };

  const uploadFiles = async (files) => {
    const promises = await files.map(async (file, index) => {
      if (!has(file, 'status')) {
        const formData = new FormData();
        formData.append('attachment', file);
        formData.append('dir', 'Banner');
        return await API.fileUpload(formData)
          .then((res) => {
            files[index] = { label: file.label, ...res.data.data };
            return { label: file.label, status: 1, ...res.data.data };
          })
          .catch((err) => {
            console.log(err.response);
          });
      } else {
        return file;
      }
    });

    const responses = await Promise.all(promises);
    return responses;
  };

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmitAccountSettings)}>
        <FormGroup tag="fieldset">
          <h3>Account Credentials</h3>
          <FormGroup row>
            <Label md="3">Username</Label>
            <Col md="9">
              <Controller
                name="username"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <Input {...field} type="text" invalid={!isEmpty(errors.username)} />
                )}
              />
              <FormFeedback style={{ display: errors.username ? 'block' : 'none' }}>
                {errors.username?.message}
              </FormFeedback>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label md="3">Email</Label>
            <Col md="9">
              <Controller
                name="email"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <Input {...field} type="text" invalid={!isEmpty(errors.email)} />
                )}
              />
              <FormFeedback style={{ display: errors.email ? 'block' : 'none' }}>
                {errors.email?.message}
              </FormFeedback>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label md="3">Mobile Number</Label>
            <Col md="3">
              <Controller
                name="mobile_code"
                defaultValue="+63"
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <ReactSelect
                    inputRef={ref}
                    value={optionCountryCodes.find((c) => c.value === value)}
                    onChange={(val) => onChange(val.value)}
                    options={optionCountryCodes}
                    isOptionDisabled={(option) => option.disabled}
                  />
                )}
              />
              {errors.mobile_code && <FormFeedback>{errors.mobile_code?.message}</FormFeedback>}
            </Col>
            <Col md="6">
              <Controller
                control={control}
                name="mobile_number"
                render={({ field }) => (
                  <Input {...field} type="text" invalid={!isEmpty(errors.mobile_number)} />
                )}
              />
              {errors.mobile_number && <FormFeedback>{errors.mobile_number?.message}</FormFeedback>}
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label md="3">Password</Label>
            <Col md="9">
              <strong>Password will be auto-generated</strong>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label md="3">First Name</Label>
            <Col md="9">
              <Controller
                name="ap_first_name"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <Input {...field} type="text" invalid={!isEmpty(errors.ap_first_name)} />
                )}
              />
              <FormFeedback style={{ display: errors.ap_first_name ? 'block' : 'none' }}>
                {errors.ap_first_name?.message}
              </FormFeedback>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label md="3">Last Name</Label>
            <Col md="9">
              <Controller
                name="ap_last_name"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <Input {...field} type="text" invalid={!isEmpty(errors.ap_last_name)} />
                )}
              />
              <FormFeedback style={{ display: errors.ap_last_name ? 'block' : 'none' }}>
                {errors.ap_last_name?.message}
              </FormFeedback>
            </Col>
          </FormGroup>
        </FormGroup>
      </Form>
      <hr />
      <FormAppDisplay />
    </>
  );
};

export default FormAppSettings;

const schemaAppDisplay = yup.object().shape({
  company_background: yup.string()
});

const FormAppDisplay = (props) => {
  const { id } = useParams();

  const {
    watch,
    formState: { errors },
    handleSubmit,
    control
  } = useForm({
    defaultValues: { company_background: '' },
    resolver: yupResolver(schemaAppDisplay)
  });

  const [editable, setEditable] = useState(false);

  const enableEdit = () => {
    setEditable(!editable);
  };

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const onSubmitAppSettings = async (data) => {
    setLoadingSubmit(true);
    const fileCondition = (file) => {
      return file && isFile(file[0]) && !has(file[0], 'id');
    };

    const params = new URLSearchParams();

    if (fileCondition(data.company_logo)) {
      let res = await uploadFiles([data.company_logo[0]]);
      params.append('company_logo', res[0].id);
    } else {
      params.append('company_logo', !isEmpty(data.company_logo) ? data.company_logo[0].id : '');
    }

    if (fileCondition(data.company_banner)) {
      let res = await uploadFiles([data.company_banner[0]]);
      params.append('company_banner', res[0].id);
    } else {
      params.append(
        'company_banner',
        !isEmpty(data.company_banner) ? data.company_banner[0].id : ''
      );
    }

    params.append('company_background', data.company_background);
    API.updateIssuerAppSettings(id, params)
      .then((res) => {
        Notification.success('App Display Update', 'App Display successfully updated');
        setLoadingSubmit(false);
        setEditable(false);
      })
      .catch((err) => {
        Notification.error(err);
        setLoadingSubmit(false);
      });
  };

  const uploadFiles = async (files) => {
    const promises = await files.map(async (file, index) => {
      if (!has(file, 'status')) {
        const formData = new FormData();
        formData.append('attachment', file);
        formData.append('dir', 'Banners');
        return await API.fileUpload(formData)
          .then((res) => {
            files[index] = { label: file.label, ...res.data.data };
            return { label: file.label, status: 1, ...res.data.data };
          })
          .catch((err) => {
            console.log(err.response);
          });
      } else {
        return file;
      }
    });

    const responses = await Promise.all(promises);
    return responses;
  };

  return (
    <Form onSubmit={handleSubmit(onSubmitAppSettings)}>
      <h3>App Display</h3>
      <FormGroup tag="fieldset" disabled={!editable}>
        <FormGroup row>
          <Label md="3">Company Logo</Label>
          <Col md="9">
            <Controller
              name="company_logo"
              control={control}
              render={({ field: { onChange, value, ref } }) => {
                return (
                  <FileUpload
                    inputRef={ref}
                    onDrop={onChange}
                    onChange={onChange}
                    imagefile={value}
                    invalid={!isEmpty(errors.company_logo)}
                  />
                );
              }}
            />
            {errors.company_logo && (
              <FormFeedback style={{ display: 'block' }}>
                {errors.company_logo?.message}
              </FormFeedback>
            )}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Company Banner</Label>
          <Col md="9">
            <Controller
              name="company_banner"
              control={control}
              render={({ field: { onChange, value, ref } }) => {
                return (
                  <FileUpload
                    inputRef={ref}
                    onDrop={onChange}
                    onChange={onChange}
                    imagefile={value}
                    invalid={!isEmpty(errors.company_banner)}
                  />
                );
              }}
            />
            {errors.company_banner && (
              <FormFeedback style={{ display: 'block' }}>
                {errors.company_banner?.message}
              </FormFeedback>
            )}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Company Background</Label>
          <Col md="9">
            <Controller
              control={control}
              name="company_background"
              render={({ field }) => (
                <Input {...field} type="textarea" invalid={!isEmpty(errors.company_background)} />
              )}
            />
            {errors.company_background && (
              <FormFeedback>{errors.company_background?.message}</FormFeedback>
            )}
          </Col>
        </FormGroup>
      </FormGroup>

      <Row>
        <Col>
          {!editable ? (
            <a type="button" onClick={enableEdit} className={`btn btn-primary float-right`}>
              Edit
            </a>
          ) : (
            <button
              type="submit"
              className={`btn ${loadingSubmit ? 'btn-gray' : 'btn-primary'} float-right`}
              disabled={loadingSubmit}
            >
              {loadingSubmit ? <i className="fas fa-spinner fa-spin"></i> : 'Save'}
            </button>
          )}
        </Col>
      </Row>
    </Form>
  );
};
